/*!
 * Copyright © 2018-2022. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { isElementNull } from '../../../../utils';
import { SnackbarService } from '../../../core/components';
import {
  AssetMarkerInfo,
  EMapContextMenuType,
  EPermissionStatus,
  EPlotType,
  EReportTypeId,
  EViewReplayType,
  GarminMarkerInfo,
  GeofenceMarkerInfo,
  IAccountSettings,
  ICoordinates,
  IDriverDetailsParams,
  IEditAssetParams,
  IEditDriverParams,
  IEditPlaceParams,
  IEditVehicleParams,
  IFindNearestParams,
  IGarminStopHistoryParams,
  IMapContextMenuData,
  IMapContextMenuPermissions,
  INavigationEnabledVehicles,
  IRunReportParams,
  IScrollItem,
  IUserFeatures,
  IUserPermissions,
  IViewGarminWindowParams,
  IViewReplayParameters,
  IWorkOrderParams,
  LatLng,
  MarkerInfo,
  VehicleMarkerInfo
} from '../../../core/models';
import { DomHelperService } from '../../../core/services';
import {
  CloseDriverMessageModal,
  GetSelectedStatusItemId,
  GetVehicleSpecification,
  GetVehicleWithEsn,
  OpenDriverMessageModal,
  OpenRoadsideAssistanceModal,
  OpenTroubleshootingPanel,
  RedirectToLocationHistory,
  RemoveFromMap,
  ScrollToItem,
  TriggerCreateGeofence,
  TriggerCreateGeofenceForVehicle,
  TriggerDriverDispatch,
  TriggerEditAsset,
  TriggerEditDriver,
  TriggerEditPlace,
  TriggerEditVehicle,
  TriggerEditWorkOrder,
  TriggerFindNearest,
  TriggerGetDirectionsFrom,
  TriggerGetDirectionsFromVehicle,
  TriggerGetDirectionsTo,
  TriggerGetDirectionsToVehicle,
  TriggerRunReport,
  TriggerSendStop,
  TriggerViewGarminWindow,
  TriggerViewReplay,
  TriggerWorkOrderDetails,
  TriggerWorkOrderStatusHistory,
  TryUpdateZoomToMarker,
  UpdateStreetViewLocation,
  UpdateZoomToLocation
} from '../../../core/store/actions';
import {
  getIsPingMVPEnabled,
  getMapContextMenuPermissions,
  getNavigationEnabledVehicles,
  getSelectedVehicleIds,
  getUserAccountSettings,
  getUserFeatures,
  getUserPermissions
} from '../../../core/store/selectors';
import { IAppState } from '../../../core/store/state';
import { DriverMessageComponent, GarminStopHistoryComponent, SendToGarminComponent } from '../../components';

@Component({
  selector: 'fm-overlay-map-menus',
  templateUrl: './overlay-map-menus.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverlayMapMenusComponent implements OnInit, OnDestroy {
  @Input()
  public menuData: IMapContextMenuData<MarkerInfo>;
  @Input()
  public menuType: EMapContextMenuType;

  @Output()
  public menuClosed: EventEmitter<void> = new EventEmitter();

  @ViewChild('coordinatesCopiedContentTemplate')
  coordinatesCopiedContentTemplate: TemplateRef<any>;

  public userPermissions$: Observable<IUserPermissions>;
  public userFeatures$: Observable<IUserFeatures>;
  public accountSettings$: Observable<IAccountSettings>;
  public userMenuPermissions$: Observable<IMapContextMenuPermissions>;
  public vehiclesIds$: Observable<string[]>;
  public vehicles$: Observable<INavigationEnabledVehicles[]>;
  public isPingMVPEnabled$: Observable<boolean>;
  public isPermissionEnabled = EPermissionStatus.Full;
  public isPermissionReadOnly = EPermissionStatus.ReadOnly;
  public isPermissionDisabled = EPermissionStatus.Disabled;
  public EMapContextMenuType = EMapContextMenuType;
  private _isTextMessagingEnabled: boolean;

  private readonly _unsubscribe$ = new Subject<void>();

  constructor(
    private readonly _store: Store<IAppState>,
    private readonly _domHelperService: DomHelperService,
    private readonly _snackbarService: SnackbarService,
    private readonly _dialog: MatLegacyDialog
  ) {}

  public ngOnInit(): void {
    this.userPermissions$ = this._store.pipe(select(getUserPermissions));
    this.userFeatures$ = this._store.pipe(select(getUserFeatures));
    this.accountSettings$ = this._store.pipe(select(getUserAccountSettings));
    this.vehiclesIds$ = this._store.pipe(
      select(getSelectedVehicleIds),
      map(ids => [...ids])
    );
    this.vehicles$ = this._store.pipe(select(getNavigationEnabledVehicles));
    this.userMenuPermissions$ = this._store.pipe(select(getMapContextMenuPermissions));
    this.isPingMVPEnabled$ = this._store.pipe(select(getIsPingMVPEnabled));
    this._setDriverMessage();
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public get vehicleId(): number {
    return this.menuType === EMapContextMenuType.Garmin
      ? (<GarminMarkerInfo>this.menuData.markerInfo).vehicleId
      : this.menuData.markerInfo.id;
  }

  public get isPoweredAsset(): boolean {
    return this.vehicleId && this.menuData.markerInfo?.type === EPlotType.poweredAsset;
  }

  public get driverId(): number {
    return (<VehicleMarkerInfo>this.menuData.markerInfo).driverId;
  }

  public get assetId(): number {
    return (<AssetMarkerInfo>this.menuData.markerInfo).id;
  }

  public closeMenu(): void {
    this.menuType = null;
    this.menuClosed.emit();
  }

  public openDriverDispatch(): void {
    this._store.dispatch(new TriggerDriverDispatch());
  }

  public openFindNearest(): void {
    this.vehiclesIds$.pipe(take(1)).subscribe((vehiclesIds: string[]) => {
      const params: IFindNearestParams = {
        m: '2',
        v: vehiclesIds,
        np: `(${this.menuData.clickLatLng.lat}, ${this.menuData.clickLatLng.lng})`
      };
      this._store.dispatch(new TriggerFindNearest(params));
    });
  }

  public openCreateGeofence(): void {
    this._store.dispatch(new TriggerCreateGeofence(this.menuData.clickLatLng));
  }

  public openCreateGeofenceForVehicle(): void {
    const vehicleId = this.menuData.markerInfo && this.menuData.markerInfo.id ? this.menuData.markerInfo.id : undefined;

    this._store.dispatch(new TriggerCreateGeofenceForVehicle(vehicleId));
  }

  public openGetDirectionTo(): void {
    this._store.dispatch(new TriggerGetDirectionsTo(this.menuData.clickLatLng));
  }

  public openGetDirectionFrom(): void {
    this._store.dispatch(new TriggerGetDirectionsFrom(this.menuData.clickLatLng));
  }

  public openGetDirectionToVehicle(): void {
    const vehicleId = this.menuData.markerInfo && this.menuData.markerInfo.id ? this.menuData.markerInfo.id : undefined;

    if (!isElementNull(vehicleId)) {
      this._store.dispatch(new TriggerGetDirectionsToVehicle(vehicleId));
    }
  }

  public openGetDirectionFromVehicle(): void {
    const vehicleId = this.menuData.markerInfo && this.menuData.markerInfo.id ? this.menuData.markerInfo.id : undefined;

    if (!isElementNull(vehicleId)) {
      this._store.dispatch(new TriggerGetDirectionsFromVehicle(vehicleId));
    }
  }

  public openLocationToGarmin(data: LatLng): void {
    const coordinates: ICoordinates = {
      Latitude: data.lat,
      Longitude: data.lng
    };
    this._dialog.open(SendToGarminComponent, { data: { coordinates } });
  }

  public openStreetView(): void {
    this._store.dispatch(
      new UpdateStreetViewLocation({
        lat: this.menuData.clickLatLng.lat,
        lng: this.menuData.clickLatLng.lng
      })
    );
  }

  public zoomTo() {
    if (this.menuType === EMapContextMenuType.Map) {
      this._store.dispatch(
        new UpdateZoomToLocation({
          lat: this.menuData.clickLatLng.lat,
          lng: this.menuData.clickLatLng.lng
        })
      );
    } else {
      this._store.dispatch(
        new TryUpdateZoomToMarker({
          latLng: this.menuData.markerInfo.latLng,
          id: this.menuData.markerInfo.id,
          type: this.menuData.markerInfo.type,
          title: this.menuData.markerInfo.title
        })
      );
    }
  }

  public openVehicleReplay(): void {
    if (this.vehicleId) {
      this._openReplay(this.vehicleId, EViewReplayType.Vehicle);
    }
  }

  public openAssetReplay(): void {
    if (this.assetId) {
      this._openReplay(this.assetId, EViewReplayType.Asset);
    }
  }

  public openRunDailyReport(): void {
    if (this.vehicleId) {
      const params: IRunReportParams = {
        reportType: EReportTypeId.Daily,
        vehicleId: this.vehicleId,
        vehicleType: this.isPoweredAsset ? EPlotType.poweredAsset : EPlotType.vehicle
      };
      this._store.dispatch(new TriggerRunReport(params));
    }
  }

  public openRunDetailedReport(): void {
    if (this.vehicleId) {
      const params: IRunReportParams = {
        reportType: EReportTypeId.Detailed,
        vehicleId: this.vehicleId,
        vehicleType: this.isPoweredAsset ? EPlotType.poweredAsset : EPlotType.vehicle
      };
      this._store.dispatch(new TriggerRunReport(params));
    }
  }

  public removeFromLiveMap(): void {
    if (this.vehicleId) {
      this._store.dispatch(new RemoveFromMap(this.vehicleId));
    }
  }

  public openEditDriver(): void {
    if (this.driverId) {
      const params: IEditDriverParams = {
        driverId: this.driverId
      };
      this._store.dispatch(new TriggerEditDriver(params));
    }
  }

  public openEditVehicle(): void {
    if (this.vehicleId) {
      const params: IEditVehicleParams = {
        vehicleId: this.vehicleId
      };
      this._store.dispatch(new TriggerEditVehicle(params));
    }
  }

  public openEditAsset(): void {
    if (this.assetId) {
      const params: IEditAssetParams = {
        assetId: this.assetId
      };
      this._store.dispatch(new TriggerEditAsset(params));
    }
  }

  public openSendDriverMessage(): void {
    if (this.driverId) {
      const driverDetailsParams: IDriverDetailsParams = {
        driverId: this.driverId,
        isTextMessagingEnabled: this._isTextMessagingEnabled
      };
      this._store.dispatch(new OpenDriverMessageModal(driverDetailsParams));
      this._dialog.open(DriverMessageComponent, { data: { driverId: this.driverId } });
    }
  }

  public sendStop(): void {
    // @TODO pending on Garmin Team
    if (this.vehicleId) {
      this._store.dispatch(new TriggerSendStop(this.vehicleId));
    }
  }

  public roadsideAssistance(): void {
    this._store.dispatch(new OpenRoadsideAssistanceModal(this.vehicleId));
  }

  public troubleshootVehicle(): void {
    if (this.vehicleId) {
      this._store.dispatch(new GetVehicleSpecification(this.vehicleId));
      this._store.dispatch(new GetVehicleWithEsn(this.vehicleId));
      this._loadHealthCheckForItem(this.vehicleId, EPlotType.vehicle);
    }
  }

  public troubleshootAsset(): void {
    if (this.assetId) {
      this._loadHealthCheckForItem(this.assetId, EPlotType.asset);
    }
  }

  public viewStops(): void {
    this._openGarminWindow();
  }

  public openViewMessages(): void {
    if (this.vehicleId) {
      const params: IViewGarminWindowParams = {
        uiMode: '2',
        vehicleId: this.vehicleId
      };
      this._store.dispatch(new TriggerViewGarminWindow(params));
    }
  }

  public openEditPlace(): void {
    const placeId = this.menuData.markerInfo.id;
    const placeType = (<GeofenceMarkerInfo>this.menuData.markerInfo).placeType;
    if (placeId && !isElementNull(placeType)) {
      const params: IEditPlaceParams = {
        id: placeId,
        type: placeType
      };
      this._store.dispatch(new TriggerEditPlace(params));
    }
  }

  public closeDriverMessageModal(): void {
    this._store.dispatch(new CloseDriverMessageModal());
  }

  public openDispatchWindow(): void {
    this._openGarminWindow();
  }

  public openViewStatusHistory(): void {
    const stopId = this.menuData.markerInfo.id;
    if (this.vehicleId && stopId) {
      const garminStopHistoryParams: IGarminStopHistoryParams = {
        StopId: stopId,
        VehicleId: this.vehicleId
      };
      this._dialog.open(GarminStopHistoryComponent, { data: { garminStopHistoryParams } });
    }
  }

  public openEditWorkOrder(): void {
    const workOrderId = this.menuData.markerInfo.id;
    if (workOrderId) {
      this._store.dispatch(new TriggerEditWorkOrder(workOrderId));
    }
  }

  public openWorkOrderDetails(): void {
    const workOrderId = this.menuData.markerInfo.id;
    if (workOrderId) {
      const params: IWorkOrderParams = {
        workOrderId
      };
      this._store.dispatch(new TriggerWorkOrderDetails(params));
    }
  }

  public openWorkOrderStatusHistory(): void {
    const workOrderId = this.menuData.markerInfo.id;
    if (workOrderId) {
      const params: IWorkOrderParams = {
        workOrderId
      };
      this._store.dispatch(new TriggerWorkOrderStatusHistory(params));
    }
  }

  public onCopyCoordinates(coordinatesToCopy: string): void {
    this._domHelperService.copyToClipboard(coordinatesToCopy);
    this._snackbarService.open(this.coordinatesCopiedContentTemplate, 4000);
  }

  public openLocationHistory(): void {
    this._store.dispatch(new RedirectToLocationHistory(this.assetId));
  }

  private _openReplay(replayTypeId: number, viewReplayType: EViewReplayType) {
    const viewReplayParameters: IViewReplayParameters = {
      id: replayTypeId,
      type: viewReplayType
    };
    this._store.dispatch(new TriggerViewReplay(viewReplayParameters));
  }

  private _openGarminWindow() {
    if (this.vehicleId) {
      const params: IViewGarminWindowParams = {
        uiMode: '1',
        vehicleId: this.vehicleId
      };
      this._store.dispatch(new TriggerViewGarminWindow(params));
    }
  }

  private _setDriverMessage(): void {
    this.userMenuPermissions$.pipe(takeUntil(this._unsubscribe$)).subscribe((permissions: IMapContextMenuPermissions) => {
      this._isTextMessagingEnabled = permissions ? permissions.textMessaging : false;
    });
  }

  private _loadHealthCheckForItem(itemId: number, type: EPlotType) {
    this._store.dispatch(new GetSelectedStatusItemId(itemId));
    this._store.dispatch(new OpenTroubleshootingPanel());
    this._store.dispatch(new ScrollToItem({ id: itemId, type: type } as IScrollItem));
    this.zoomTo();
  }
}
